// JourneyDetail — Public/auth page showing a full journey with step-by-step progress.
// Route: /journey/:slug
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import * as Lucide from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { useT, LanguageSwitcher } from "@/i18n/LanguageContext";

const RED = "#C8102E";
const GOLD = "#D4AF37";
const CHARCOAL = "#1A1A1A";

export default function JourneyDetail() {
  const { slug } = useParams();
  const { t } = useT();
  const [journey, setJourney] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/journeys/${slug}`).then((r) => setJourney(r.data)).catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || t("jd.not_found")));
    api.get("/journey/mine").then((r) => {
      const mine = (r.data?.items || []).find((it) => it.journey?.slug === slug);
      if (mine) setProgress(mine.progress);
    }).catch(() => {});
  }, [slug, t]);
  useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="min-h-screen bg-white grid place-items-center px-5">
      <div className="text-center"><div className="text-5xl mb-3">🔍</div><h1 className="font-display font-black text-2xl">{error}</h1><Link to="/dashboard" className="mt-4 inline-block text-sm font-semibold underline">{t("jd.back_dashboard")}</Link></div>
    </div>
  );
  if (!journey) return <div className="min-h-screen grid place-items-center font-mono text-sm text-zinc-500">{t("jd.loading")}</div>;

  const steps = (journey.steps || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const completed = new Set(progress?.completed_step_ids || []);
  const done = steps.filter((s) => completed.has(s.step_id)).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
  const nextStepId = (steps.find((s) => !completed.has(s.step_id)) || {}).step_id;

  const openWA = (tpl) => window.open(`https://wa.me/60137003381?text=${encodeURIComponent(tpl || "Hi MIRESCO!")}`, "_blank", "noopener");

  const completeStep = async (stepId) => {
    setBusy(true);
    try {
      await api.post(`/journey/${slug}/start`).catch(() => {});
      await api.post(`/journey/${slug}/steps/${stepId}/complete`);
      load();
      toast.success(t("jd.step_done_toast"));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="jd-back"><Lucide.ArrowLeft className="w-4 h-4" /> {t("jd.back")}</Link>
          <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 truncate max-w-[40%]">{journey.track} · {t("jd.journey")}</div>
          <LanguageSwitcher />
        </div>
      </header>

      <section className="border-b border-zinc-200 text-white relative overflow-hidden" style={{ background: CHARCOAL }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full mb-4" style={{ background: `${GOLD}30`, color: GOLD }}>
            <Lucide.MapPin className="w-3.5 h-3.5" /> {t("jd.guided")} · {journey.estimated_days} {t("jd.days")}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="jd-title">{journey.title}</h1>
          {journey.subtitle && <p className="mt-3 text-white/80 max-w-2xl">{journey.subtitle}</p>}
          <div className="mt-6 max-w-2xl">
            <div className="flex items-baseline justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">{t("jd.progress")}</div>
              <div className="text-sm font-display font-black" style={{ color: GOLD }} data-testid="jd-progress-text">{done} / {steps.length} · {pct}%</div>
            </div>
            <div className="h-2 bg-white/10 overflow-hidden"><div className="h-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${RED} 0%, ${GOLD} 100%)` }} data-testid="jd-progress-bar" /></div>
          </div>
          {journey.reward_note && (
            <div className="mt-6 text-sm" style={{ color: GOLD }}><Lucide.Award className="w-4 h-4 inline mr-1" /> {t("jd.reward")}: {journey.reward_note}</div>
          )}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 lg:px-10 py-10 lg:py-14 space-y-3">
        {steps.map((s, i) => {
          const isDone = completed.has(s.step_id);
          const isNext = s.step_id === nextStepId;
          const isLocked = !isDone && !isNext;
          return (
            <div
              key={s.step_id}
              className={`border p-4 sm:p-5 flex items-start gap-4 ${isDone ? "border-emerald-200 bg-emerald-50" : isNext ? "border-zinc-900 bg-white shadow-[6px_6px_0_0_#C8102E]" : "border-zinc-200 bg-zinc-50 opacity-70"}`}
              data-testid={`jd-step-${s.step_id}`}
            >
              <div className={`grid place-items-center w-9 h-9 shrink-0 border ${isDone ? "bg-emerald-600 border-emerald-600 text-white" : isNext ? "bg-zinc-900 border-zinc-900 text-white" : "bg-white border-zinc-300 text-zinc-500"}`}>
                {isDone ? <Lucide.Check className="w-5 h-5" /> : isLocked ? <Lucide.Lock className="w-4 h-4" /> : <span className="font-black text-sm">{i + 1}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-base sm:text-lg tracking-tight leading-tight">{s.title}</div>
                {s.subtitle && <div className="text-sm text-zinc-600 mt-1">{s.subtitle}</div>}
                {s.body && !isLocked && <div className="text-sm text-zinc-700 mt-2 whitespace-pre-line">{s.body}</div>}
                {s.reward_note && <div className="mt-2 text-[10px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>{t("jd.reward_step")}: {s.reward_note}</div>}
                {isNext && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.kind === "cta_wa" && s.wa_template && (
                      <button onClick={() => openWA(s.wa_template)} className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1" style={{ background: "#25D366" }} data-testid={`jd-step-wa-${s.step_id}`}>
                        <Lucide.MessageCircle className="w-3 h-3" /> {t("jd.send_wa")}
                      </button>
                    )}
                    {s.external_url && (
                      <a href={s.external_url} target={s.external_url.startsWith("/") ? "_self" : "_blank"} rel="noopener noreferrer" className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1" style={{ background: CHARCOAL }} data-testid={`jd-step-link-${s.step_id}`}>
                        {t("jd.open")} <Lucide.ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {s.video_url && <a href={s.video_url} target="_blank" rel="noopener noreferrer" className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1" style={{ background: RED }}>{t("jd.watch")} <Lucide.Play className="w-3 h-3" /></a>}
                    {s.download_url && <a href={s.download_url} target="_blank" rel="noopener noreferrer" className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1" style={{ background: CHARCOAL }}>{t("jd.download")} <Lucide.Download className="w-3 h-3" /></a>}
                    <button onClick={() => completeStep(s.step_id)} disabled={busy} className="text-white text-xs font-semibold px-3 py-2 inline-flex items-center gap-1 disabled:opacity-50" style={{ background: RED }} data-testid={`jd-step-complete-${s.step_id}`}>
                      {busy ? t("jd.saving") : t("jd.mark_done")} <Lucide.Check className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}
